import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles, Link } from '@material-ui/core';
import { CloudOff } from '@material-ui/icons';

import StatusMessage from 'components/StatusMessage';
import { REDSHELF_SOLVE_LINK } from 'constants';
import FileDownloadOff from 'icons/FileDownloadOff.icon';

const useStyles = makeStyles((theme) => {
  return {
    /* Styles applied to the root element. */
    root: {},
    /* Download Unavailable styles */
    statusMessage: {
      padding: theme.spacing(2, 3),

      lineHeight: theme.typography.pxToRem(24),
      letterSpacing: theme.typography.pxToRem(0.15) + '!important',
    },
    downloadUnavailableList: {
      listStyle: 'none',
    },
    downloadUnavailableListTitle: {
      fontWeight: theme.typography.fontWeightMedium,
      paddingTop: theme.spacing(4),
    },
  };
});

const getCurrentMessage = (
  offlineAvailable,
  deviceInfoDescriptions,
  offlineLimitReached,
  isOffline,
  isPageUnavailable,
  classes,
) => {
  if (!offlineAvailable) {
    return {
      icon: <FileDownloadOff />,
      title: 'Download Unavailable',
      body: (
        <>
          Offline access is not available for this material. Learn more about
          offline capabilities on our{' '}
          <Link
            href={REDSHELF_SOLVE_LINK}
            underline="always"
            target="_blank"
            rel="noreferrer"
          >
            RedShelf Solve page
          </Link>
          .
        </>
      ),
    };
  }

  if (isPageUnavailable) {
    return {
      icon: <CloudOff />,
      title: `You're Offline`,
      body: 'To access this page, go back online or, if available, download this chapter for offline use. ',
    };
  }

  if (offlineLimitReached) {
    return {
      icon: <FileDownloadOff />,
      title: 'Download Unavailable',
      body: (
        <div>
          <p>
            You’ve exceeded your offline session limit for this material. You
            will need to remove your downloads from other devices or browsers to
            download content for offline for this session.
          </p>
          <div>
            <p className={classes.downloadUnavailableListTitle}>
              Active Offline Sessions:
            </p>
            <ul className={classes.downloadUnavailableList}>
              {deviceInfoDescriptions?.map((deviceDescription) => {
                return (
                  <li key={deviceDescription} className="noTranslate">
                    {deviceDescription}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ),
    };
  }

  if (isOffline) {
    return {
      icon: <CloudOff />,
      title: `You're Offline`,
      body: 'Your network connection is unavailable. While offline, you will not be able to download the contents of this material. ',
    };
  }

  return {};
};

const OfflineUnavailableMessage = ({
  deviceInfoDescriptions = [],
  isOffline,
  offlineLimitReached,
  offlineAvailable,
  isPageUnavailable,
  ...props
}) => {
  const classes = useStyles();

  const { icon, title, body } = getCurrentMessage(
    offlineAvailable,
    deviceInfoDescriptions,
    offlineLimitReached,
    isOffline,
    isPageUnavailable,
    classes,
  );

  return (
    <StatusMessage
      className={classes.statusMessage}
      icon={icon}
      title={title}
      {...props}
    >
      {body}
    </StatusMessage>
  );
};

OfflineUnavailableMessage.propTypes = {
  deviceInfoDescriptions: PropTypes.arrayOf(PropTypes.string),
  isOffline: PropTypes.bool,
  offlineLimitReached: PropTypes.bool,
  offlineAvailable: PropTypes.bool,
  isPageUnavailable: PropTypes.bool,
};

export default OfflineUnavailableMessage;
